// ─────────────────────────────────────────────────────────────
// useSupabase
// Extracted from App.js.
//
// Trades are kept in React state only (useState) — not localStorage.
// The trade list can exceed localStorage's ~5MB limit for active accounts,
// causing silent write failures that left the app with an empty trades array.
// customStrategies remain in localStorage (small, safe to persist).
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { normaliseTrade } from "../lib/format";
import { SUPABASE_URL, SUPABASE_ANON_KEY, authFetch } from "../lib/supabase";

const LS_SCHEMA_VERSION = "fos_ls_schema_version";
const CURRENT_LS_VERSION = "4";

function clearStaleCacheIfNeeded(uid) {
  try {
    const stored = localStorage.getItem(LS_SCHEMA_VERSION);
    if (stored === CURRENT_LS_VERSION) return;
    const keys = ["fos_trades_", "fos_custom_strategies_"];
    const suffixes = [uid || "demo", "demo"];
    keys.forEach(k => suffixes.forEach(s => localStorage.removeItem(k + s)));
    localStorage.setItem(LS_SCHEMA_VERSION, CURRENT_LS_VERSION);
    console.log("[useSupabase] Cleared stale local cache; will refetch from database.");
  } catch (e) {
    console.warn("[useSupabase] Could not clear local cache:", e);
  }
}

export function useSupabase(userId) {
  const isConfigured = !!(SUPABASE_URL && SUPABASE_ANON_KEY);
  const uid = userId;

  clearStaleCacheIfNeeded(uid);

  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(true); // true until first fetch completes

  const [customStrategies, setCustomStrategies] = useLocalStorage(
    `fos_custom_strategies_${uid || "demo"}`,
    []
  );

  const fetchTrades = useCallback(async () => {
    if (!isConfigured || !uid) return;
    setTradesLoading(true);
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/trades?user_id=eq.${uid}&order=trade_date.desc,created_at.desc&select=*&limit=10000`
      );
      const data = await res.json();
      setTrades(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchTrades error:", e);
    } finally {
      setTradesLoading(false);
    }
  }, [isConfigured, uid]);

  const fetchCustomStrategies = useCallback(async () => {
    if (!isConfigured || !uid) return;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/custom_strategies?user_id=eq.${uid}&order=created_at.desc&select=*`
      );
      const data = await res.json();
      setCustomStrategies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchCustomStrategies error:", e);
    }
  }, [isConfigured, uid, setCustomStrategies]);

  const addTrade = useCallback(async (tradeData) => {
    const normalised = normaliseTrade(tradeData);
    const newTrade = {
      ...normalised,
      id: Date.now().toString(),
      user_id: uid,
      created_at: new Date().toISOString()
    };

    if (!isConfigured || !uid) {
      setTrades(prev => [newTrade, ...prev]);
      return newTrade;
    }

    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/trades`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify({ ...normalised, user_id: uid })
      });
      const saved = await res.json();
      if (res.ok && saved?.[0]) {
        setTrades(prev => [saved[0], ...prev]);
        return saved[0];
      } else {
        console.error("addTrade Supabase error:", res.status, saved);
      }
    } catch (e) {
      console.error("addTrade error:", e);
    }
    setTrades(prev => [newTrade, ...prev]);
    return newTrade;
  }, [isConfigured, uid]);

  const deleteTrade = useCallback(async (id) => {
    if (isConfigured && uid) {
      try {
        await authFetch(
          `${SUPABASE_URL}/rest/v1/trades?id=eq.${id}&user_id=eq.${uid}`,
          { method: "DELETE" }
        );
      } catch (e) {
        console.error("deleteTrade error:", e);
      }
    }
    setTrades(prev => prev.filter(t => t.id !== id));
  }, [isConfigured, uid]);

  const updateTrade = useCallback(async (id, updatedData) => {
    let updatedTrade = null;

    setTrades(prev => {
      const existing = prev.find(t => t.id === id);
      if (!existing) return prev;
      updatedTrade = normaliseTrade({ ...existing, ...updatedData });
      return prev.map(t => (t.id === id ? updatedTrade : t));
    });

    if (isConfigured && uid) {
      try {
        const normalisedUpdate = normaliseTrade(updatedData);
        const res = await authFetch(
          `${SUPABASE_URL}/rest/v1/trades?id=eq.${id}&user_id=eq.${uid}`,
          {
            method: "PATCH",
            headers: { Prefer: "return=representation" },
            body: JSON.stringify(normalisedUpdate)
          }
        );
        const saved = await res.json();
        if (res.ok && saved?.[0]) {
          setTrades(prev => prev.map(t => (t.id === id ? saved[0] : t)));
          return saved[0];
        } else {
          console.error("Update failed:", res.status, saved);
        }
      } catch (e) {
        console.error("updateTrade error:", e);
      }
    }
    return updatedTrade;
  }, [isConfigured, uid]);

  const addCustomStrategy = useCallback(async (strategy) => {
    const localStrategy = {
      user_id: uid,
      name: strategy.name,
      id: Date.now().toString(),
      confirmation_factors: strategy.confirmation_factors || null,
      created_at: new Date().toISOString(),
    };

    if (!isConfigured || !uid) {
      setCustomStrategies(prev => [localStrategy, ...prev]);
      return localStrategy;
    }

    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/custom_strategies`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify({
          user_id: uid,
          name: strategy.name,
          confirmation_factors: strategy.confirmation_factors || null,
        })
      });
      const saved = await res.json();
      if (res.ok && saved?.[0]) {
        setCustomStrategies(prev => [saved[0], ...prev]);
        return saved[0];
      } else {
        console.error("addCustomStrategy Supabase error:", res.status, saved);
      }
    } catch (e) {
      console.error("addCustomStrategy error:", e);
    }

    setCustomStrategies(prev => [localStrategy, ...prev]);
    return localStrategy;
  }, [isConfigured, uid, setCustomStrategies]);

  const deleteCustomStrategy = useCallback(async (id) => {
    if (isConfigured && uid) {
      try {
        await authFetch(
          `${SUPABASE_URL}/rest/v1/custom_strategies?id=eq.${id}&user_id=eq.${uid}`,
          { method: "DELETE" }
        );
      } catch (e) {
        console.error("deleteCustomStrategy error:", e);
      }
    }
    setCustomStrategies(prev => prev.filter(s => s.id !== id));
  }, [isConfigured, uid, setCustomStrategies]);

  useEffect(() => {
    if (uid) {
      fetchTrades();
      fetchCustomStrategies();
    }
  }, [uid, fetchTrades, fetchCustomStrategies]);

  return {
    trades,
    tradesLoading,
    addTrade,
    deleteTrade,
    updateTrade,
    refetch: fetchTrades,
    customStrategies,
    addCustomStrategy,
    deleteCustomStrategy,
    isConfigured
  };
}




// ─────────────────────────────────────────────────────────────
// MANAGE CUSTOM STRATEGIES PAGE
// ─────────────────────────────────────────────────────────────
// =========================================================================
// MANAGE STRATEGIES - Toggle Built-in + Custom + Daily Limit
// =========================================================================
